import { useEffect, useState } from "react";
import useSummary from "../../../../CustomizedDashboard/useSummary";
import PriorityTag from "../../../../Components/PriorityTag/PriorityTag";
import useWidgetFilterNavigator from "../../../../CustomizedDashboard/useWidgetFilterNavigator";
import { TextButton, formatNumber } from "@SeemplicityDev/shared";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../General/useSeverityAsPriority";
import { capitalize } from "lodash-es";
import "./FindingsBySeverityBar.scss";

export type FindingsBySeverityProps = {
  selectedFilter?: {
    label?: string;
    value: {
      id: string;
      query: string;
    };
  };
  selectedScope?: {
    value: {
      id: string;
      query: string;
    };
  };
  viewMode?: boolean;
};

export type SummaryItem = [string, number];

type CountItem = {
  severity: string;
  count: number;
  onClick: () => void;
  fill: string;
  label: string;
};

const FindingsBySeverityBar: React.FC<FindingsBySeverityProps> = ({
  selectedFilter,
  selectedScope,
  viewMode = false,
}) => {
  const { severityAsPriorityActive, getSeverityField } =
    useSeverityAsPriority();
  const [total, setTotal] = useState<number | null>(null);
  const [counts, setCounts] = useState<CountItem[] | null>(null);

  const { summaryData: data, summaryLoading } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.id"],
      group_by: ["finding.severity"],
      aggregate_functions: ["COUNT"],
      round_functions: ["ROUND"],
      filters_config: {
        scopesid: selectedScope?.value?.id,
        filtersjson: selectedFilter?.value?.query,
      },
    },
  });

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: selectedFilter?.value?.query || null,
    filterId: selectedFilter?.value?.id || null,
    scopeToExecute: selectedScope?.value?.query || null,
    scopeId: selectedScope?.value?.id || null,
    newTab: true,
  });

  useEffect(() => {
    if (!!data) {
      let max = 0;
      const displayedCounts = (data as SummaryItem[])
        .toReversed()
        .map((el: SummaryItem) => {
          const [severity, count] = el;
          max += count;
          return {
            severity,
            count,
            fill: getSeverityField(severity, SeverityFieldsEnum.COLOR),
            label: capitalize(
              getSeverityField(severity, SeverityFieldsEnum.NAME)
            ),
            onClick: () => onFilterClick([["in", "severity", severity]], null),
          };
        });

      setTotal(max);
      setCounts(displayedCounts);
    }
  }, [data]);

  useEffect(() => {
    if (summaryLoading) {
      setCounts(null);
      setTotal(null);
    }
  }, [summaryLoading]);

  return (
    <div
      data-testid="findings-by-severity-bar"
      className={`findings-by-severity`}
    >
      {!summaryLoading ? (
        <>
          {total !== null &&
            (viewMode || total === 0 ? (
              <span
                data-testid="findings-total"
                className={`findings-by-severity-total ${viewMode ? "is-view-mode" : ""}`}
              >
                {total === 0
                  ? "0 Findings"
                  : `${formatNumber(total)} Finding${total > 1 ? "s" : ""}`}
              </span>
            ) : (
              <TextButton
                wrapperClassName="findings-by-severity-total"
                label={`${formatNumber(total)} Finding${total > 1 ? "s" : ""}`}
                onClick={() => onFilterClick([], null)}
                buttonStyle={"textLink"}
                data-testid="findings-total"
                type="button"
                data-analytics-event="user_click"
                data-analytics-props={`{"name": "all_finding_priority_summary"}`}
              />
            ))}
          {!!counts?.length && (
            <ul
              data-testid="findings-by-severity-list"
              className="findings-by-severity-list"
            >
              {counts?.map((el: CountItem) => (
                <li
                  key={el.severity}
                  data-testid={`findings-by-severity-item-${el.severity}`}
                  className="findings-by-severity-item"
                >
                  {severityAsPriorityActive ? (
                    <>
                      <PriorityTag severity={el.severity} size="s" />
                      {viewMode ? (
                        <span
                          className="findings-by-severity-count"
                          data-testid={"findings-count"}
                        >
                          {`${formatNumber(el.count)} Finding${el.count > 1 ? "s" : ""}`}
                        </span>
                      ) : (
                        <TextButton
                          wrapperClassName="findings-by-severity-link"
                          label={`${formatNumber(el.count)} Finding${el.count > 1 ? "s" : ""}`}
                          onClick={el.onClick}
                          buttonStyle={"textLink"}
                          data-testid={"findings-count"}
                          type="button"
                          data-analytics-event="user_click"
                          data-analytics-props={`{"name": "finding_priority_summary"}`}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <div
                        data-testid="severity-dot"
                        className={`severity-dot`}
                        style={{ backgroundColor: el.fill }}
                      />
                      {viewMode ? (
                        <span
                          className="findings-by-severity-count"
                          data-testid={"findings-count"}
                        >
                          {`${formatNumber(el.count)} ${el.label}`}
                        </span>
                      ) : (
                        <TextButton
                          wrapperClassName="findings-by-severity-link"
                          label={`${formatNumber(el.count)} ${el.label}`}
                          onClick={el.onClick}
                          buttonStyle={"textLink"}
                          data-testid={"findings-count"}
                          type="button"
                          data-analytics-event="user_click"
                          data-analytics-props={`{"name": "finding_priority_summary"}`}
                        />
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <span className="findings-by-severity-total is-loading">
          <i className="spinner seem-icon seem-icon-spinner" /> Findings
        </span>
      )}
    </div>
  );
};

export default FindingsBySeverityBar;
